import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import AttendanceWidget from "@/components/AttendanceWidget";
import { cn } from "@/lib/utils";
import { BellRing, CalendarPlus, Pencil, CalendarClock, FileText, Users, MapPin, CheckCheck } from "lucide-react";
import { toast } from "sonner";

// Phone layout of the employee dashboard (shown below the lg breakpoint only).
// Brand colours taken from the IntenCiv logo: navy #042C53, sky blue #56C2F4, deep blue #205991.
const NAVY = "#042C53", SKY = "#56C2F4", DEEP = "#205991";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function formatShiftTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

function Chip({ children }) {
  return <span className="text-[11px] text-white rounded-full px-2.5 py-0.5 border" style={{ borderColor: SKY }}>{children}</span>;
}

function NotificationsCard() {
  const qc = useQueryClient();
  const { data: items } = useQuery({
    queryKey: ["myNotifications"], queryFn: async () => (await api.get("/my/notifications")).data,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["myNotifications"] });
    qc.invalidateQueries({ queryKey: ["unreadNotifications"] });
  };
  const markAll = useMutation({
    mutationFn: async () => (await api.post("/my/notifications/read-all")).data,
    onSuccess: refresh,
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const unread = (items || []).filter((n) => !n.read_at);

  if (unread.length === 0) {
    return (
      <Link to="/notifications" data-testid="mdash-notifications">
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3">
          <CheckCheck className="h-5 w-5 shrink-0" style={{ color: DEEP }} />
          <p className="text-sm text-slate-600">You're all caught up. <span className="font-medium" style={{ color: DEEP }}>View past notifications</span></p>
        </div>
      </Link>
    );
  }
  return (
    <div className="rounded-2xl bg-white p-3.5" style={{ border: `1.5px solid ${SKY}` }} data-testid="mdash-notifications">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <BellRing className="h-5 w-5" style={{ color: DEEP }} />
          <span className="text-sm font-semibold text-navy">Notifications</span>
        </div>
        <span className="text-[11px] font-semibold bg-red-500 text-white rounded-full px-2.5 py-0.5" data-testid="mdash-unread-count">{unread.length} unread</span>
      </div>
      {unread.slice(0, 3).map((n) => (
        <div key={n.id} className="flex gap-2.5 py-2 border-t border-slate-100">
          <span className="mt-1.5 h-2 w-2 rounded-full bg-red-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-navy truncate">{n.title}</p>
            <p className="text-xs text-slate-500 line-clamp-2 break-words">{n.message}</p>
          </div>
        </div>
      ))}
      {unread.length > 3 && <p className="text-xs text-slate-400 pt-1">+{unread.length - 3} more</p>}
      <div className="flex gap-2 mt-2">
        <Link to="/notifications" className="flex-1 h-10 rounded-xl text-[13px] font-semibold text-white grid place-items-center" style={{ background: NAVY }}>
          Read all notifications
        </Link>
        <button onClick={() => markAll.mutate()} disabled={markAll.isPending} aria-label="Mark all as read"
          className="h-10 w-10 rounded-xl border border-slate-200 grid place-items-center text-slate-500">
          <CheckCheck className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function AttendanceCard() {
  const { data: status } = useQuery({
    queryKey: ["attendanceStatus"], queryFn: async () => (await api.get("/my/attendance/status")).data,
  });
  const locations = status?.assigned_locations || [];
  const shift = status?.shift;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3.5" data-testid="mdash-attendance">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-slate-500">Today's attendance</p>
        <Link to="/attendance" className="text-xs font-medium" style={{ color: DEEP }}>History</Link>
      </div>
      {status?.field_staff ? (
        <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Field / Remote Staff — no fixed shift</p>
      ) : (
        <>
          <p className="text-[22px] font-semibold text-navy mt-1">
            {shift ? formatShiftTime(shift.shift_start) : "No shift set"}
            {shift && <span className="text-[13px] font-normal text-slate-500"> · {shift.shift_hours} hrs shift</span>}
          </p>
          {locations.length > 0 && (
            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {locations.map((l) => l.name).join(", ")}</p>
          )}
        </>
      )}
      {status?.cover_today && (
        <p className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          Today you are the backup for <span className="font-semibold">{status.cover_today.for_name}</span>, duty starts at {formatShiftTime(status.cover_today.shift_start)}.
        </p>
      )}
      <div className="mt-3"><AttendanceWidget /></div>
    </div>
  );
}

function Action({ to, icon: Icon, label, className }) {
  return (
    <Link to={to} className={cn("rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-2.5 active:bg-slate-50", className)}>
      <span className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: "#E6F1FB" }}>
        <Icon className="h-[18px] w-[18px]" style={{ color: DEEP }} />
      </span>
      <span className="text-[13px] font-semibold text-navy">{label}</span>
    </Link>
  );
}

export default function MobileDashboard({ data }) {
  const c = data.current || {};
  const name = data.identity.name || "";
  const initials = name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");
  const balances = data.leave?.balances || [];

  return (
    <div className="space-y-3" data-testid="mobile-dashboard">
      <div className="rounded-3xl p-4" style={{ background: NAVY }}>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full grid place-items-center text-lg font-semibold shrink-0" style={{ background: SKY, color: NAVY }}>{initials}</div>
          <div className="min-w-0">
            <p className="text-xs" style={{ color: SKY }}>{greeting()}</p>
            <p className="text-[19px] font-semibold text-white leading-tight truncate">{name}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3.5">
          <Chip>{data.identity.employee_id}</Chip>
          {c.designation && <Chip>{c.designation}</Chip>}
          {c.project_site && <Chip>{c.project_site}</Chip>}
        </div>
      </div>

      <NotificationsCard />
      <AttendanceCard />

      {balances.length > 0 && (
        <div>
          <p className="text-[13px] text-slate-500 mb-2 ml-0.5">Leave balance{data.leave?.pending > 0 && <span className="text-amber-600"> · {data.leave.pending} pending</span>}</p>
          <div className="grid grid-cols-2 gap-2.5">
            {balances.map((b) => (
              <Link to="/leave" key={b.code} className="rounded-2xl bg-slate-100/80 p-3">
                <p className="text-xs text-slate-500">{b.name || b.code}</p>
                <p className="text-2xl font-semibold text-navy mt-0.5">{b.available ?? "—"}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-[13px] text-slate-500 mb-2 ml-0.5">Quick actions</p>
        <div className="grid grid-cols-2 gap-2.5">
          <Action to="/leave" icon={CalendarPlus} label="Apply leave" />
          <Action to="/requests" icon={Pencil} label="Correction" />
          <Action to="/holidays" icon={CalendarClock} label="Holidays" />
          <Action to="/documents" icon={FileText} label="Documents" />
          {data.is_reporting_officer && <Action to="/team" icon={Users} label="My team" className="col-span-2" />}
        </div>
      </div>
    </div>
  );
}
